import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, MessagesSquare, Users } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { STAT_CARD_BASE } from '../../components/ui/StatCard';
import authService from '../../services/authService';

/**
 * The Simulations tab's first screen: choose the team simulation or a paired micro
 * simulation. Each option appears only when the trainer has that feature (the API
 * enforces the same flags; this is presentation). With only one option there is no
 * choice to make, so the page redirects to it.
 */

const OPTIONS = [
  {
    key: 'simulations',
    path: '/trainer/simulations/team',
    title: 'Simulation',
    tag: 'Team',
    tagClass: 'bg-cyan-50 text-cyan-700 ring-cyan-100',
    Icon: Users,
    text: 'The whole room in teams. Decide together, reveal at once, see the business impact.',
  },
  {
    key: 'microSims',
    path: '/trainer/micro-sims',
    title: 'Micro simulation',
    tag: 'Paired',
    tagClass: 'bg-violet-50 text-violet-700 ring-violet-100',
    Icon: MessagesSquare,
    text: 'Two people practise a workplace conversation. Each gets personal skill feedback.',
  },
];

const SimulationsHomePage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isTrainer = user?.role === 'trainer';
  const available = OPTIONS.filter((o) => !isTrainer || user?.features?.[o.key]);

  // Nothing to choose between: go straight to the only option (old links and
  // bookmarks to /trainer/simulations keep working).
  if (available.length === 1) return <Navigate to={available[0].path} replace />;

  return (
    <DashboardLayout title="Simulations">
      <div className="mx-auto w-full max-w-4xl px-4 py-6 lg:px-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">What would you like to run?</h2>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Pick the kind of practice that fits your session.
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {available.map(({ key, path, title, tag, tagClass, Icon, text }) => (
            <button
              key={key}
              type="button"
              onClick={() => navigate(path)}
              className={`${STAT_CARD_BASE} group flex flex-col p-5 text-left transition-shadow hover:shadow-lg`}
            >
              <div className="flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                  <Icon size={19} strokeWidth={1.75} />
                </span>
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${tagClass}`}>
                  {tag}
                </span>
              </div>
              <h3 className="mt-4 text-base font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
              <p className="mt-1 flex-1 text-sm text-neutral-500 dark:text-neutral-400">{text}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all">
                Open <ArrowRight size={15} />
              </span>
            </button>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SimulationsHomePage;
